(() => {
  const initialise = (root) => {
    if (root.dataset.widerrufInitialised === "true") {
      return;
    }

    root.dataset.widerrufInitialised = "true";
    root.hidden = false;

    const endpoint = root.dataset.widerrufEndpoint;
    const copy = root.dataset;
    const form = root.querySelector('[data-widerruf-step="1"]');
    const review = root.querySelector('[data-widerruf-step="2"]');
    const success = root.querySelector("[data-widerruf-success]");
    const status = root.querySelector("[data-widerruf-status]");
    const alternative = root.querySelector("[data-widerruf-alternative]");
    const editButton = root.querySelector("[data-widerruf-edit]");
    const confirmButton = root.querySelector("[data-widerruf-confirm]");
    const confirmationLabel = confirmButton.textContent;
    let submitting = false;
    let token = null;
    let tokenNotBefore = 0;
    let tokenExpiresAt = 0;
    let tokenRequest = null;

    const setStatus = (message = "") => {
      status.textContent = message;
    };

    const focus = (element) => {
      element.focus();
    };

    const hideAlternative = () => {
      alternative.hidden = true;
    };

    const showAlternative = (shouldFocus = true) => {
      alternative.hidden = false;
      setStatus(copy.copyAlternative);
      if (shouldFocus) {
        focus(alternative);
      }
    };

    const parseJson = async (response) => {
      try {
        return await response.json();
      } catch {
        return {};
      }
    };

    const requestToken = async () => {
      if (tokenRequest) {
        return tokenRequest;
      }

      setStatus(copy.copyTokenLoading);
      tokenRequest = fetch(new URL("/api/token", endpoint), {
        method: "GET",
        mode: "cors",
        credentials: "omit",
        cache: "no-store",
        headers: { Accept: "application/json" },
      })
        .then(async (response) => {
          const payload = await parseJson(response);
          if (!response.ok || typeof payload.token !== "string") {
            throw new Error("token_unavailable");
          }

          const receivedAt = Date.now();
          token = payload.token;
          tokenNotBefore =
            receivedAt + Number(payload.not_before_seconds || 0) * 1000;
          tokenExpiresAt =
            receivedAt + Number(payload.expires_in_seconds || 0) * 1000;
          setStatus();
        })
        .finally(() => {
          tokenRequest = null;
        });

      return tokenRequest;
    };

    const showStep = (step) => {
      form.hidden = step !== 1;
      review.hidden = step !== 2;
      success.hidden = true;
      const heading = root.querySelector(
        `[data-widerruf-step="${step}"] [data-widerruf-step-heading]`,
      );
      focus(heading);
    };

    const clearFieldErrors = () => {
      root.querySelectorAll("[data-widerruf-error]").forEach((error) => {
        error.hidden = true;
        error.textContent = "";
      });
      root.querySelectorAll("[data-widerruf-field]").forEach((field) => {
        field.removeAttribute("aria-invalid");
        const errorId = `Widerruf-${field.name}-error`;
        const describedBy = (field.getAttribute("aria-describedby") || "")
          .split(/\s+/)
          .filter((id) => id && id !== errorId);
        if (describedBy.length > 0) {
          field.setAttribute("aria-describedby", describedBy.join(" "));
        } else {
          field.removeAttribute("aria-describedby");
        }
      });
    };

    const fieldErrorMessage = (fieldName, errorCode) => {
      if (errorCode === "required") {
        return copy.copyFieldRequired;
      }
      if (fieldName === "email" && errorCode === "invalid") {
        return copy.copyFieldEmail;
      }
      if (errorCode === "too_long") {
        return copy.copyFieldTooLong;
      }
      return copy.copyFieldInvalid;
    };

    const showFieldErrors = (fields) => {
      clearFieldErrors();
      showStep(1);
      let firstInvalidField = null;

      Object.entries(fields).forEach(([fieldName, errorCode]) => {
        const field = form.elements.namedItem(fieldName);
        const error = root.querySelector(
          `[data-widerruf-error="${fieldName}"]`,
        );
        if (!(field instanceof HTMLElement) || !error) {
          return;
        }

        error.textContent = fieldErrorMessage(fieldName, errorCode);
        error.hidden = false;
        field.setAttribute("aria-invalid", "true");
        const describedBy = new Set(
          (field.getAttribute("aria-describedby") || "")
            .split(/\s+/)
            .filter(Boolean),
        );
        describedBy.add(error.id);
        field.setAttribute("aria-describedby", [...describedBy].join(" "));
        firstInvalidField ||= field;
      });

      setStatus(copy.copyValidationError);
      if (firstInvalidField) {
        focus(firstInvalidField);
      }
    };

    const setSubmitting = (busy) => {
      submitting = busy;
      confirmButton.disabled = busy;
      editButton.disabled = busy;
      confirmButton.toggleAttribute("aria-busy", busy);
      confirmButton.textContent = busy
        ? copy.copySubmitting
        : confirmationLabel;
    };

    const values = () => ({
      name: form.elements.namedItem("name").value.trim(),
      email: form.elements.namedItem("email").value.trim(),
      bestellnummer: form.elements.namedItem("bestellnummer").value.trim(),
      artikel: form.elements.namedItem("artikel").value.trim(),
      website: form.elements.namedItem("website").value,
    });

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      clearFieldErrors();
      hideAlternative();
      setStatus();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const formValues = values();
      Object.entries(formValues).forEach(([fieldName, value]) => {
        const summary = root.querySelector(
          `[data-widerruf-summary="${fieldName}"]`,
        );
        if (summary) {
          summary.textContent =
            fieldName === "artikel" && value === ""
              ? copy.copyEntireOrder
              : value;
        }
      });
      showStep(2);
    });

    editButton.addEventListener("click", () => {
      if (submitting) {
        return;
      }
      hideAlternative();
      setStatus();
      showStep(1);
    });

    confirmButton.addEventListener("click", async () => {
      if (submitting) {
        return;
      }

      hideAlternative();
      const now = Date.now();
      if (!token || now >= tokenExpiresAt) {
        try {
          await requestToken();
          setStatus(copy.copyTokenRenewed);
        } catch {
          showAlternative();
        }
        return;
      }
      if (now < tokenNotBefore) {
        setStatus(copy.copyTokenWait);
        return;
      }

      setSubmitting(true);
      setStatus(copy.copySubmitting);
      const payload = { token, ...values() };

      try {
        const response = await fetch(new URL("/api/widerruf", endpoint), {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        const responsePayload = await parseJson(response);

        if (response.status === 201) {
          const receivedAt = new Date(responsePayload.eingangszeitpunkt);
          if (
            typeof responsePayload.vorgangsnummer !== "string" ||
            Number.isNaN(receivedAt.getTime())
          ) {
            throw new Error("invalid_success_response");
          }

          form.hidden = true;
          review.hidden = true;
          success.hidden = false;
          root.querySelector("[data-widerruf-vorgangsnummer]").textContent =
            responsePayload.vorgangsnummer;
          const time = root.querySelector(
            "[data-widerruf-eingangszeitpunkt]",
          );
          time.dateTime = responsePayload.eingangszeitpunkt;
          time.textContent = new Intl.DateTimeFormat(
            document.documentElement.lang || "de-DE",
            {
              dateStyle: "long",
              timeStyle: "medium",
              timeZone: "Europe/Berlin",
            },
          ).format(receivedAt);
          setStatus(success.querySelector("h2").textContent);
          focus(root.querySelector("[data-widerruf-success-heading]"));
          return;
        }

        if (
          response.status === 422 &&
          responsePayload.fields &&
          typeof responsePayload.fields === "object"
        ) {
          showFieldErrors(responsePayload.fields);
          return;
        }

        if (
          response.status === 403 &&
          responsePayload.error === "invalid_token"
        ) {
          token = null;
          await requestToken();
          setStatus(copy.copyTokenRenewed);
          return;
        }

        if (response.status === 429 || response.status === 503) {
          showAlternative();
          return;
        }

        setStatus(copy.copyGenericError);
      } catch {
        showAlternative();
      } finally {
        if (!success.hidden) {
          submitting = false;
        } else {
          setSubmitting(false);
        }
      }
    });

    requestToken().catch(() => showAlternative(false));
  };

  const initialiseAll = (scope = document) => {
    scope.querySelectorAll("[data-widerruf-form]").forEach(initialise);
  };

  initialiseAll();
  document.addEventListener("shopify:section:load", (event) => {
    initialiseAll(event.target);
  });
})();
